import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { reportAnalyticsError } from '../utils/analytics-error-reporting.util';
import { isCaptureMode } from '../utils/report-capture.util';
import { ViewerAuthService } from './viewer-auth.service';

/**
 * Fire-and-forget report view beacon for the analytics SPA (jep.to).
 *
 * Operational notes:
 * - At most one POST per browser tab session per reportId (sessionStorage dedupe).
 * - Skipped when ?capture=1 (Playwright PDF capture) or tracking is disabled.
 * - deliveryId comes from email links (?d=); sessionId is generated per tab.
 * - Public shares: unsigned POST with Crockford reportId; BE validates access.
 * - Private shares: Authorization: JeptoViewer when viewer JWT is present.
 * - fetch errors are logged only; they must never block report rendering.
 */
export const REPORT_VIEW_SESSION_STORAGE_KEY = 'jepto-report-view-session';
export const REPORT_VIEW_TRACKED_PREFIX = 'jepto-report-view-tracked:';
export const DELIVERY_ID_QUERY_PARAM = 'd';

export interface ReportViewTrackingParams {
	reportId: string;
	deliveryId?: string;
}

export function parseDeliveryIdFromUrl(
	search: string = typeof window !== 'undefined' ? window.location.search : ''
): string | undefined {
	const value = new URLSearchParams(search).get(DELIVERY_ID_QUERY_PARAM);
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
}

function trackedStorageKey(reportId: string): string {
	return `${REPORT_VIEW_TRACKED_PREFIX}${reportId}`;
}

@Injectable({ providedIn: 'root' })
export class ReportViewTrackingService {
	private readonly viewerAuth = inject(ViewerAuthService);

	trackView(params: ReportViewTrackingParams): void {
		if (!environment.reportViewTrackingEnabled) {
			return;
		}

		if (isCaptureMode()) {
			return;
		}

		const reportId = params.reportId?.trim();
		if (!reportId) {
			return;
		}

		const reportViewUrl = environment.reportViewUrl?.trim();
		if (!reportViewUrl) {
			return;
		}

		if (this.isReportTrackedInSession(reportId)) {
			return;
		}

		const deliveryId = params.deliveryId ?? parseDeliveryIdFromUrl();
		const sessionId = this.getOrCreateSessionId();
		const body: Record<string, string> = {
			reportId,
			sessionId,
		};

		if (deliveryId) {
			body.deliveryId = deliveryId;
		}

		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};
		const viewerJwt = this.viewerAuth.getJwtForReport(reportId);
		if (viewerJwt) {
			headers.Authorization = this.#viewerAuthorizationHeader(viewerJwt);
		}

		this.markReportTrackedInSession(reportId);

		fetch(reportViewUrl, {
			method: 'POST',
			headers,
			body: JSON.stringify(body),
		}).catch(err => {
			reportAnalyticsError(
				'[ReportViewTracking] Failed to track report view',
				err,
				{ feature: 'analytics', operation: 'trackView' }
			);
		});
	}

	#viewerAuthorizationHeader(token: string): string {
		const trimmed = token.trim();
		const withoutScheme = trimmed.replace(/^(JeptoViewer|Bearer)\s+/i, '');
		return `JeptoViewer ${withoutScheme}`;
	}

	private isReportTrackedInSession(reportId: string): boolean {
		try {
			return sessionStorage.getItem(trackedStorageKey(reportId)) === '1';
		} catch {
			return false;
		}
	}

	private markReportTrackedInSession(reportId: string): void {
		try {
			sessionStorage.setItem(trackedStorageKey(reportId), '1');
		} catch {
			// sessionStorage unavailable (private mode, SSR)
		}
	}

	private getOrCreateSessionId(): string {
		try {
			const existing = sessionStorage.getItem(
				REPORT_VIEW_SESSION_STORAGE_KEY
			);
			if (existing?.trim()) {
				return existing;
			}
			const sessionId = crypto.randomUUID();
			sessionStorage.setItem(REPORT_VIEW_SESSION_STORAGE_KEY, sessionId);
			return sessionId;
		} catch {
			return crypto.randomUUID();
		}
	}
}
